class StoreLocator {
  constructor(sectionId, config) {
    this.sectionId = sectionId;
    this.config = config;
    this.map = null;
    this.markers = [];
    this.markerCluster = null;
    this.autocomplete = null;
    this.userPosition = null;
    this.showingAllStores = false;
    this.allStores = [];
    this.nearbyStores = [];
    this.visibleStores = [];
    this.infoWindow = null;
    this.backendUrl =
      "https://store-locator-shopify-app-d9f525452f2f.herokuapp.com";
    this.userMarker = null;
    this.searchMarker = null;
    this.selectedProvinces = [];
    this.selectedStoreTypes = ["original_social", "manual", "modified_social"]; // New: Default to show all
    this.manualStoreColor = config.manual_store_color || "#FF0000";
    this.searchTerm = "";
    this.provinceFilterOpen = false;
    this.storeTypeFilterOpen = false;
    this.resizeObserver = new ResizeObserver(() => this.handleResize());
    this.preloader = document.getElementById(`preloader-${sectionId}`);
    this.wrapper = document.querySelector(
      `#store-locator-${sectionId} .store-locator-wrapper`
    );

    // New: Debounced functions for performance
    this.updateVisibleStoresDebounced = this.debounce(
      () => this.updateVisibleStores(),
      300
    );
    this.filterStoresDebounced = this.debounce(() => this.filterStores(), 300);

    this.setHeights();
    window.addEventListener("resize", () => this.setHeights());

    this.setupSearch(); // Setup search listeners early
    this.init();
  }

  // New: Debounce utility
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func.apply(this, args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  setHeights() {
    const mapElement = document.getElementById(`map-${this.sectionId}`);
    const resultsContainer = document.querySelector(
      `#results-column-${this.sectionId} .stores-scroll-container`
    );

    if (window.innerWidth <= 768) {
      // Mobile layout
      if (mapElement) mapElement.style.height = "50vh";
      if (resultsContainer) resultsContainer.style.maxHeight = "50vh";
    } else {
      // Desktop layout - match heights
      if (mapElement && resultsContainer) {
        const mapHeight = mapElement.offsetHeight;
        resultsContainer.style.maxHeight = `${mapHeight}px`;
      }
    }
  }

  async init() {
    try {
      performance.mark("locator_start");
      await this.loadGoogleMaps();

      await this.initMap(); // Await map initialization
      this.initAutocomplete();
      this.setupEventListeners();
      this.setupBrandSelector();

      const scrollContainer = document.querySelector(
        `#results-column-${this.sectionId} .stores-scroll-container`
      );
      if (scrollContainer) this.resizeObserver.observe(scrollContainer);
    } catch (error) {
      console.error("Initialization failed:", error);
      this.showMapError(this.sectionId, "Failed to initialize store locator");
    } finally {
      this.hideLoader();
    }
  }

  handleResize() {
    this.setHeights();
    this.updateVisibleStores();
  }

  hideLoader() {
    if (this.preloader) {
      performance.mark("locator_loaded");
      performance.measure(
        "locator_load_time",
        "locator_start",
        "locator_loaded"
      );

      this.preloader.style.opacity = "0";

      if (this.wrapper) {
        this.wrapper.style.opacity = "1";
        this.wrapper.style.visibility = "visible";
      }

      setTimeout(() => {
        document.body.classList.add("locator-loaded");
        this.preloader.style.display = "none";
      }, 300);
    }
  }

  async loadGoogleMaps() {
    return new Promise((resolve, reject) => {
      if (window.google && window.google.maps) return resolve();

      const callbackName = `gmapsCallback_${Date.now()}`;
      const script = document.createElement("script");

      script.src = `${this.backendUrl}/maps-api?brand=${this.config.brand}&callback=${callbackName}&libraries=places,geometry`;
      script.async = true;
      script.defer = true;
      script.onerror = () => {
        document.head.removeChild(script);
        reject(new Error("Failed to load Google Maps API"));
      };

      window[callbackName] = () => {
        delete window[callbackName];
        if (!window.google || !google.maps) {
          reject(new Error("Google Maps library not properly loaded"));
        } else {
          resolve();
        }
      };

      document.head.appendChild(script);
      setTimeout(() => {
        if (!window.google) reject(new Error("Google Maps loading timed out"));
      }, 20000);
    });
  }

  async initMap() {
    try {
      const { Map } = await google.maps.importLibrary("maps");
      const mapElement = document.getElementById(`map-${this.sectionId}`);
      const defaultCenter = { lat: -28.4793, lng: 24.6727 };

       const mapStyles = [
        { elementType: "geometry", stylers: [{ color: "#3c3c3c" }] }, // Lighter grey background
        { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
        { elementType: "labels.text.fill", stylers: [{ color: "#b0b0b0" }] }, // Brighter text for contrast
        { elementType: "labels.text.stroke", stylers: [{ color: "#3c3c3c" }] },
        { featureType: "administrative", elementType: "geometry", stylers: [{ color: "#757575" }, { visibility: "off" }] },
        { featureType: "administrative.country", elementType: "labels.text.fill", stylers: [{ color: "#9e9e9e" }] },
        { featureType: "administrative.land_parcel", stylers: [{ visibility: "off" }] },
        { featureType: "administrative.locality", elementType: "labels.text.fill", stylers: [{ color: "#cccccc" }] },
        { featureType: "poi", stylers: [{ visibility: "off" }] },
        { featureType: "poi", elementType: "labels.text.fill", stylers: [{ color: "#757575" }] },
        { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#2c2c2c" }] }, // Darker parks
        { featureType: "road", elementType: "geometry.fill", stylers: [{ color: "#212121" }] }, // Dark roads
        { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#2c2c2c" }] },
        { featureType: "road", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
        { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#8a8a8a" }] },
        { featureType: "road.arterial", elementType: "geometry", stylers: [{ color: "#2c2c2c" }] },
        { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#1a1a1a" }] }, // Even darker highways
        { featureType: "transit", stylers: [{ visibility: "off" }] },
        { featureType: "water", elementType: "geometry", stylers: [{ color: "#111111" }] }, // Almost black water
        { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#3d3d3d" }] },
      ];

      this.map = new google.maps.Map(mapElement, {
        center: defaultCenter,
        zoom: 6,
        styles: mapStyles, 
        
      });

      const geolocatePromise = new Promise((resolve, reject) => {
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            timeout: 5000,
            maximumAge: 60000,
            enableHighAccuracy: true,
          });
        } else {
          reject(new Error("Geolocation is not supported."));
        }
      });

      try {
        const position = await geolocatePromise;
        this.userPosition = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        await this.centerMapOnUser(position, false); // Don't re-process stores yet
      } catch (error) {
        console.warn("Initial geolocation failed:", error.message);
        this.userPosition = null;
      }

      // Now, fetch stores. This will use userPosition if it was found.
      await this.refreshStores();

      // Use debounced handler
      this.map.addListener("bounds_changed", this.updateVisibleStoresDebounced);
      this.map.addListener("zoom_changed", this.updateVisibleStoresDebounced);
    } catch (error) {
      console.error("Map initialization failed:", error);
      this.showMapError(this.sectionId, "Failed to initialize the map.");
    }
  }

  createCustomMarkerElement(color) {
    const element = document.createElement("div");
    element.innerHTML = `
        <svg width="42" height="42" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z" fill="${color}"/>
        </svg>`;
    return element;
  }

  async centerMapOnUser(position, process = true) {
    if (!position || !this.map) return;

    if (this.userMarker) this.userMarker.map = null;

    this.userPosition = {
      lat: position.coords.latitude,
      lng: position.coords.longitude,
    };

    this.map.setCenter(this.userPosition);
    this.map.setZoom(12);

    const userMarkerIcon = {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent('<svg...fill="orange"/></svg>'),
      scaledSize: new google.maps.Size(40, 40),
      anchor: new google.maps.Point(20, 40)
    };
    
    this.userMarker = new google.maps.Marker({
      position: this.userPosition,
      map: this.map,
      title: "You are here",
      icon: userMarkerIcon
    });

    if (process) {
      await this.processStores();
      if (this.nearbyStores.length > 0) {
        const storesToShow = this.nearbyStores.slice(0, 5);
        this.displayStores(storesToShow);
      }
    }
  }

  initAutocomplete() {
    const input = document.getElementById(`autocomplete-${this.sectionId}`);
    if (!input) return;
    if (!google.maps.places) return;

    try {
      this.autocomplete = new google.maps.places.Autocomplete(input, {
        types: ["geocode"],
        fields: ["geometry", "name"],
        componentRestrictions: { country: "ZA" },
      });

      this.autocomplete.addListener("place_changed", async () => {
        const place = this.autocomplete.getPlace();
        if (!place.geometry || !place.geometry.location) return;

        this.userPosition = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        };

        this.map.setCenter(this.userPosition);
        this.map.setZoom(12);

        if (this.searchMarker) this.searchMarker.map = null;

        const { AdvancedMarkerElement } = await google.maps.importLibrary(
          "marker"
        );
        this.searchMarker = new google.maps.Marker({
          position: this.userPosition,
          map: this.map,
          title: "Searched Location", // or place.name
      });

        await this.processStores();
        if (this.nearbyStores.length > 0) {
          const storesToShow = this.nearbyStores.slice(0, 5);
          this.displayStores(storesToShow);
        }
      });

      input.addEventListener("keydown", (e) => {
        if (e.key === "Enter") e.preventDefault();
      });
    } catch (error) {
      console.error("Autocomplete initialization failed:", error);
    }
  }

  showResultsLoader() {
    const resultsColumn = document.getElementById(
      `results-column-${this.sectionId}`
    );
    if (resultsColumn) resultsColumn.classList.add("is-loading");
  }

  hideResultsLoader() {
    const resultsColumn = document.getElementById(
      `results-column-${this.sectionId}`
    );
    if (resultsColumn) resultsColumn.classList.remove("is-loading");
  }

  setupEventListeners() {
    const locationBtn = document.querySelector(
      `.store-locator-location-btn[data-section-id="${this.sectionId}"]`
    );
    if (locationBtn)
      locationBtn.addEventListener("click", () => this.useMyLocation());

    const searchIcon = document.querySelector(
      `.store-locator-search-icon[data-section-id="${this.sectionId}"]`
    );
    if (searchIcon)
      searchIcon.addEventListener("click", () => this.searchByAddress());

    const addressInput = document.getElementById(
      `autocomplete-${this.sectionId}`
    );
    if (addressInput) {
      addressInput.addEventListener("keydown", (e) => {
        if (e.key === "Enter") this.searchByAddress();
      });
    }

    const toggleBtn = document.querySelector(
      `.province-filter-toggle[data-section-id="${this.sectionId}"]`
    );
    if (toggleBtn) {
      toggleBtn.addEventListener("click", () => {
        this.provinceFilterOpen = !this.provinceFilterOpen;
        const options = toggleBtn.nextElementSibling;
        toggleBtn.setAttribute("aria-expanded", this.provinceFilterOpen);
        options.setAttribute("aria-hidden", !this.provinceFilterOpen);
        options.classList.toggle("visible", this.provinceFilterOpen);
      });
    }

    const provinceCheckboxes = document.querySelectorAll(
      `.province-checkbox[data-section-id="${this.sectionId}"]`
    );
    provinceCheckboxes.forEach((checkbox) => {
      checkbox.addEventListener("change", () => {
        this.selectedProvinces = Array.from(
          document.querySelectorAll(
            `.province-checkbox[data-section-id="${this.sectionId}"]:checked`
          )
        ).map((cb) => cb.value);
        this.applyFilters();
      });
    });

    const storeTypeToggleBtn = document.querySelector(`.store-type-filter-toggle[data-section-id="${this.sectionId}"]`);
if (storeTypeToggleBtn) {
  storeTypeToggleBtn.addEventListener("click", () => {
    this.storeTypeFilterOpen = !this.storeTypeFilterOpen;
    const options = storeTypeToggleBtn.nextElementSibling;
    storeTypeToggleBtn.setAttribute("aria-expanded", this.storeTypeFilterOpen);
    options.setAttribute("aria-hidden", !this.storeTypeFilterOpen);
  });
}

    // New: Listener for store type checkboxes
    const storeTypeCheckboxes = document.querySelectorAll(
      `.store-type-checkbox[data-section-id="${this.sectionId}"]`
    );
    storeTypeCheckboxes.forEach((checkbox) => {
      checkbox.addEventListener("change", () => {
        this.selectedStoreTypes = Array.from(
          document.querySelectorAll(
            `.store-type-checkbox[data-section-id="${this.sectionId}"]:checked`
          )
        ).map((cb) => cb.value);
        this.applyFilters();
      });
    });
  }

  setupSearch() {
    const searchInput = document.getElementById(
      `store-search-${this.sectionId}`
    );
    const clearButton = document.querySelector(
      `#store-search-${this.sectionId} + .store-search-clear`
    );

    if (searchInput) {
      // Use debounced listener for efficiency
      searchInput.addEventListener(
        "input",
        this.debounce((e) => {
          this.searchTerm = e.target.value.toLowerCase().trim();
          this.filterStores();
        }, 300)
      );
    }

    if (clearButton) {
      clearButton.addEventListener("click", () => {
        this.searchTerm = "";
        searchInput.value = "";
        this.filterStores();
      });
    }
  }

  // Updated: Central filtering and rendering function
  filterStores() {
    // `this.visibleStores` is the list of stores currently within the map bounds
    let filteredList = [...this.visibleStores];

    // Apply search term if it exists
    if (this.searchTerm) {
      filteredList = filteredList.filter((store) => {
        const searchFields = [
          store.name,
          store.address,
          store.contact_number,
          store.province,
        ]
          .join(" ")
          .toLowerCase();
        return searchFields.includes(this.searchTerm);
      });
    }

    // Call the single display function with the final, filtered list
    this.displayStores(filteredList);
  }


  // Renamed from applyProvinceFilter to be more generic
applyFilters() {
    this.showResultsLoader();
    try {
      this.clearMarkers();
      const storesToShow = this.getFilteredStoresBySelection();
      this.markers = storesToShow.map(store => this.createStoreMarker(store));
      this.updateVisibleStores();
      this.updateMapView(storesToShow);
    } catch (e) {
      console.error("Error applying filters:", e);
    } finally {
      this.hideResultsLoader();
    }
  }

  updateMapView(stores) {
    const storesToBound = stores || this.visibleStores;

    if (!this.map || storesToBound.length === 0) {
      if (this.userPosition) {
        this.map.setCenter(this.userPosition);
        this.map.setZoom(12);
      } else {
        this.map.setCenter({ lat: -28.4793, lng: 24.6727 });
        this.map.setZoom(6);
      }
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    storesToBound.forEach((store) => {
      bounds.extend(
        new google.maps.LatLng(
          parseFloat(store.latitude),
          parseFloat(store.longitude)
        )
      );
    });

    const padding = window.innerWidth <= 768 ? 50 : 100;
    this.map.fitBounds(bounds, padding);

    const zoom = this.map.getZoom();
    if (zoom > 12) this.map.setZoom(12);
  }

  // Updated: Central data flow for rendering
  updateVisibleStores() {
    if (!this.map || !this.allStores) return;

    const bounds = this.map.getBounds();
    if (!bounds) return;

    // First filter by province and store type
    let storesInSelection = this.getFilteredStoresBySelection();

    // Then filter by what's visible in the map bounds
    this.visibleStores = storesInSelection.filter((store) => {
      try {
        const storeLatLng = new google.maps.LatLng(
          parseFloat(store.latitude),
          parseFloat(store.longitude)
        );
        return bounds.contains(storeLatLng);
      } catch (error) {
        return false;
      }
    });

    // Finally, apply search term and render the list
    this.filterStores();
  }

  formatStore(store) {
    return {
      ...store,
      id: store.id || `manual_${Math.random().toString(36).substr(2, 9)}`,
      latitude: parseFloat(store.latitude),
      longitude: parseFloat(store.longitude),
      operating_hours: store.operating_hours || store.operatingHours || {},
    };
  }

  async refreshStores() {
    this.showResultsLoader();
    this.clearMarkers();
    this.clearStoreListings();

    try {
      const response = await fetch(
        `${this.backendUrl}/api/stores?brand=${this.config.brand}`
      );
      const dbStores = response.ok ? await response.json() : [];
      this.allStores = [...dbStores];

      await this.processStores();
      this.updateVisibleStores(); // This will trigger the full filter and render flow
    } catch (error)      {
      console.error("Failed to fetch stores:", error);
      this.showStoreLoadError();
    } finally {
      this.hideResultsLoader();
    }
  }

async processStores() {
    if (!this.allStores || this.allStores.length === 0) return;

    this.allStores = this.allStores.filter((store) => {
      const lat = parseFloat(store.latitude);
      const lng = parseFloat(store.longitude);
      return !isNaN(lat) && !isNaN(lng);
    });
    
    // This now correctly populates the initial markers
    this.clearMarkers();
    const storesToMark = this.getFilteredStoresBySelection();
    this.markers = storesToMark.map(store => this.createStoreMarker(store));

    if (this.userPosition) {
      this.allStores.forEach((store) => {
        const storePos = {
          lat: parseFloat(store.latitude),
          lng: parseFloat(store.longitude),
        };
        store.distance = google.maps.geometry.spherical.computeDistanceBetween(
          new google.maps.LatLng(this.userPosition),
          new google.maps.LatLng(storePos)
        );
      });
      this.nearbyStores = [...this.allStores].sort(
        (a, b) => a.distance - b.distance
      );
    } else {
      this.nearbyStores = [...this.allStores];
      this.allStores.forEach((store) => (store.distance = null));
    }
  }

  // Updated: Now accepts a list of stores to render
  async displayStores(stores) {
    const container = document.getElementById(
      `nearby-stores-${this.sectionId}`
    );
    if (!container) return;

    container.innerHTML =
      stores.length === 0
        ? '<p class="no-stores-message">No stores match your criteria.</p>'
        : "";

    stores.forEach((store) => this.createStoreListing(store, container));
    this.setHeights();
  }

  // Updated: Now filters by store type as well
  getFilteredStoresBySelection() {
    let stores = [...this.allStores];

    // 1. Filter by Store Type
    if (this.selectedStoreTypes.length > 0) {
      stores = stores.filter((store) =>
        this.selectedStoreTypes.includes(store.storeType)
      );
    }

    // 2. Filter by Province
    if (this.selectedProvinces.length > 0) {
      const countryLevelFilters = ["Namibia", "Lesotho", "Eswatini", "Botswana"];
      const selectedCountries = this.selectedProvinces.filter((selection) =>
        countryLevelFilters.includes(selection)
      );
      const selectedSaProvinces = this.selectedProvinces.filter(
        (selection) => !countryLevelFilters.includes(selection)
      );

      stores = stores.filter((store) => {
        if (selectedCountries.includes(store.country_name)) return true;
        if (selectedSaProvinces.includes(store.province)) return true;
        return false;
      });
    }

    return stores;
  }

  // Updated: Adds icon and red/bold title
  createStoreListing(store, container) {
    const storeId = `store-${
      store.id || store.name.toLowerCase().replace(/\s+/g, "-")
    }`;
    const storeDiv = document.createElement("div");
    storeDiv.id = storeId;
    storeDiv.classList.add("store-item");

    const distanceText = store.distance
      ? `<p class="store-distance">${this.formatDistance(
          store.distance
        )} away</p>`
      : "";

    let storeIcon = '';
    if (store.storeType === 'original_social' || store.storeType === 'modified_social') {
      storeIcon = `<img src="https://cdn.shopify.com/s/files/1/0987/0940/files/base-locate.png" alt="SPCC Base Store icon" class="store-title-icon">`;
    } else { // 'manual' for distributors
      storeIcon = `<svg class="distributor-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z"/></svg>`;
    }

    storeDiv.innerHTML = `
            <h3 class="store-title">${storeIcon} ${store.name}</h3>
            ${distanceText}
            <p><strong>Contact:</strong> ${store.contact_number}</p>
            <p><strong>Address:</strong> <a href="${
              store.google_maps_url
            }" target="_blank">${store.address}</a></p>
            ${this.formatOperatingHours(store.operating_hours)}
            <button style="background-color: ${this.config.buttonColor};" 
                    class="store-view-btn" 
                    data-lat="${store.latitude}" 
                    data-lng="${store.longitude}">
              View on Map
            </button>
          `;

    storeDiv.querySelector(".store-view-btn").addEventListener("click", () => {
      this.panToStore(parseFloat(store.latitude), parseFloat(store.longitude));
    });

    container.appendChild(storeDiv);
    store.markerElement = storeDiv;
  }

  
 createStoreMarker(store) {
    let icon;
    if (store.storeType === "manual") {
      const svg = `<svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z" fill="${this.manualStoreColor}"/></svg>`;
      icon = {
        url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg),
        scaledSize: new google.maps.Size(30, 30),
        anchor: new google.maps.Point(15, 30),
      };
    } else {
      icon = {
        url: "https://cdn.shopify.com/s/files/1/0987/0940/files/base-locate.png",
        scaledSize: new google.maps.Size(35, 40),
        anchor: new google.maps.Point(17, 40),
      };
    }

    const marker = new google.maps.Marker({
      position: { lat: store.latitude, lng: store.longitude },
      map: this.map,
      title: store.name,
      icon: icon,
    });

    marker.addListener("click", async () => {
      const markerLatLng = marker.getPosition();
      const clickedStore = this.allStores.find(
        (s) =>
          Math.abs(s.latitude - markerLatLng.lat()) < 0.0001 &&
          Math.abs(s.longitude - markerLatLng.lng()) < 0.0001
      );
      if (clickedStore) {
        if (this.infoWindow) this.infoWindow.close();
        this.infoWindow = new google.maps.InfoWindow({
          content: this.createInfoWindowContent(clickedStore),
        });
        await this.ensureStoreVisible(clickedStore);
        this.infoWindow.open({ anchor: marker, map: this.map });
        await this.scrollToStoreInList(clickedStore);
      }
    });

    return marker;
  }

  async ensureStoreVisible(store) {
    const storeId = `store-${
      store.id || store.name.toLowerCase().replace(/\s+/g, "-")
    }`;
    const storeElement = document.getElementById(storeId);

    if (!storeElement) {
      this.updateVisibleStores();
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
  }

  createInfoWindowContent(store) {
    return `
          <div class="info-window">
            <h3 style="font-size: 13px;">${store.name}</h3>
            <a href="${store.google_maps_url}" target="_blank" 
               style="color: ${this.config.buttonColor};"><strong>
              View on Google Maps</strong>
            </a>
          </div>
        `;
  }

  async scrollToStoreInList(store) {
    const storeId = `store-${
      store.id || store.name.toLowerCase().replace(/\s+/g, "-")
    }`;
    await new Promise((resolve) => requestAnimationFrame(resolve));
    const storeElement = document.getElementById(storeId);
    if (!storeElement) return;

    const container = document.getElementById(`nearby-stores-${this.sectionId}`);
    if (container) {
      const elementTop = storeElement.offsetTop;
      const containerHeight = container.offsetHeight;
      const scrollPosition = Math.max(0, elementTop - containerHeight / 3);
      container.scrollTo({ top: scrollPosition, behavior: "smooth" });
      this.highlightStore(storeElement);
    }
  }

  highlightStore(element) {
    element.style.display = "block";
    void element.offsetHeight;

    document
      .querySelectorAll(".store-item.highlighted")
      .forEach((el) => el.classList.remove("highlighted"));
    element.classList.add("highlighted");

    if (this.highlightTimeout) clearTimeout(this.highlightTimeout);
    this.highlightTimeout = setTimeout(
      () => element.classList.remove("highlighted"),
      3000
    );
  }

  formatDistance(distanceInMeters) {
    return distanceInMeters < 1000
      ? `${Math.round(distanceInMeters)}m`
      : `${(distanceInMeters / 1000).toFixed(1)}km`;
  }

  formatOperatingHours(hours) {
    if (!hours) return "";
    const daysOrder = [ "monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday" ];
    const formatTime = (timeStr) => timeStr?.includes(":") ? timeStr.slice(0, 5) : "";

    const groupedHours = [];
    let currentGroup = null;

    daysOrder.forEach((day) => {
      const dayHours = hours[day];
      if (!dayHours || !dayHours.open || !dayHours.close) return;
      const timeRange = `${formatTime(dayHours.open)}-${formatTime(dayHours.close)}`;

      if (!currentGroup) {
        currentGroup = { days: [day], timeRange };
      } else if (currentGroup.timeRange === timeRange) {
        currentGroup.days.push(day);
      } else {
        groupedHours.push(currentGroup);
        currentGroup = { days: [day], timeRange };
      }
    });
    if (currentGroup) groupedHours.push(currentGroup);

    const formatDayRange = (days) => {
      if (days.length === 1) return days[0].charAt(0).toUpperCase() + days[0].slice(1, 3);
      const firstDay = days[0].charAt(0).toUpperCase() + days[0].slice(1, 3);
      const lastDay = days[days.length - 1].charAt(0).toUpperCase() + days[days.length - 1].slice(1, 3);
      return `${firstDay}-${lastDay}`;
    };

    let hoursHtml = '<div class="operating-hours"><strong>Hours:</strong>';
    groupedHours.forEach((group) => {
      hoursHtml += `<p>${formatDayRange(group.days)}: ${group.timeRange}</p>`;
    });
    return hoursHtml + "</div>";
  }

  async toggleAllStores(showAll = !this.showingAllStores) {
    this.showingAllStores = showAll;
    const nearbyContainer = document.getElementById(`nearby-stores-${this.sectionId}`);
    const allContainer = document.getElementById(`all-stores-${this.sectionId}`);
    if (showAll) {
      nearbyContainer.style.display = "none";
      allContainer.style.display = "block";
      if (allContainer.children.length === 0) {
        this.allStores.forEach((store) => this.createStoreListing(store, allContainer));
      }
    } else {
      nearbyContainer.style.display = "block";
      allContainer.style.display = "none";
    }
    this.updateToggleButton();
    await new Promise((resolve) => requestAnimationFrame(resolve));
  }

  updateToggleButton() {
    const toggleBtn = document.getElementById(`toggle-all-stores-${this.sectionId}`);
    if (toggleBtn) {
      toggleBtn.textContent = this.showingAllStores ? "Show Nearby Stores" : "Show All Stores";
      toggleBtn.style.display = this.nearbyStores.length > 0 ? "section" : "none";
    }
  }

 clearMarkers() {
    this.markers.forEach((marker) => marker.setMap(null)); 
    this.markers = [];
  }

  clearStoreListings() {
    const nearbyContainer = document.getElementById(`nearby-stores-${this.sectionId}`);
    const allContainer = document.getElementById(`all-stores-${this.sectionId}`);
    if (nearbyContainer) nearbyContainer.innerHTML = "";
    if (allContainer) allContainer.innerHTML = "";
  }

  async searchByAddress() {
    this.showResultsLoader();
    const input = document.getElementById(`autocomplete-${this.sectionId}`);
    const address = input?.value.trim();
    if (!address) {
      this.hideResultsLoader();
      return;
    }
    try {
      const geocoder = new google.maps.Geocoder();
      const { results } = await geocoder.geocode({ address, componentRestrictions: { country: "ZA" } });
      if (results?.[0]?.geometry?.location) {
        this.userPosition = {
          lat: results[0].geometry.location.lat(),
          lng: results[0].geometry.location.lng(),
        };
        this.map.setCenter(this.userPosition);
        this.map.setZoom(12);
        if (this.searchMarker) this.searchMarker.map = null;
        const { AdvancedMarkerElement } = await google.maps.importLibrary("marker");
        this.searchMarker = new google.maps.Marker({
          position: this.userPosition,
          map: this.map,
          title: "Searched Location", // or place.name
        });
        await this.processStores();
        if (this.nearbyStores.length > 0) {
          this.displayStores(this.nearbyStores.slice(0, 5));
        }
      }
    } catch (error) {
      console.error("Search failed:", error);
      alert("Could not find that location. Please try a different address.");
    } finally {
      this.hideResultsLoader();
    }
  }

  async useMyLocation() {
    if (!navigator.geolocation) {
      alert("Geolocation not supported by this browser.");
      return;
    }
    const locationBtn = document.querySelector(`.store-locator-location-btn[data-section-id="${this.sectionId}"]`);
    const originalText = locationBtn.innerHTML;
    locationBtn.disabled = true;
    locationBtn.innerHTML = "Locating...";
    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        });
      });
      await this.centerMapOnUser(position);
    } catch (error) {
      alert("Could not get your location: " + this.getGeolocationError(error));
    } finally {
      locationBtn.disabled = false;
      locationBtn.innerHTML = originalText;
    }
  }

  getGeolocationError(error) {
    switch (error.code) {
      case error.PERMISSION_DENIED: return "User denied the request for geolocation.";
      case error.POSITION_UNAVAILABLE: return "Location information is unavailable.";
      case error.TIMEOUT: return "The request to get user location timed out.";
      default: return "An unknown error occurred.";
    }
  }

  panToStore(lat, lng) {
    if (this.map) {
      this.map.panTo({ lat, lng });
      this.map.setZoom(14);
    }
  }

  showMapError(sectionId, message = "Failed to load map") {
    const mapElement = document.getElementById(`map-${sectionId}`);
    if (mapElement) {
      mapElement.innerHTML = `<div class="map-error"><p>${message}</p><p>Please try again later or contact support.</p></div>`;
    }
  }

  showStoreLoadError() {
    const container = document.getElementById(`results-column-${this.sectionId}`);
    if (container) {
      container.innerHTML = `<div class="error-message"><p>Failed to load store locations. Please try again later.</p></div>`;
    }
  }

  setupBrandSelector() {
    const brandSelect = document.getElementById(`brands-${this.sectionId}`);
    if (!brandSelect) return;
    brandSelect.addEventListener("change", (e) => {
      window.storeLocatorConfig[this.sectionId].brand = e.target.value;
      this.refreshStores();
    });
  }
}

document.addEventListener("DOMContentLoaded", () => {
  Object.keys(window.storeLocatorConfig).forEach((sectionId) => {
    try {
      new StoreLocator(sectionId, window.storeLocatorConfig[sectionId]);
    } catch (error) {
      console.error(
        `Failed to initialize store locator for section ${sectionId}:`,
        error
      );
    }
  });
});